<script setup lang="ts" generic="T extends SelectInputType, ValueKey extends string, LabelKey extends string">
import {
  computed,
  toRef,
} from 'vue';

import type {
  IFormPlainDataListboxItem,
  IFormPlainDataListboxPropsChangeState,
  SelectInputType,
} from '@modules/forms/types';
import VFormPlainDataListbox
  from '@modules/forms/components/inputs/VFormPlainDataListbox/VFormPlainDataListbox.vue';

import type {
  VFormMultiselectBoxEmits,
  VFormMultiselectBoxProps,
} from './types';
import { useInputInnerValue } from '../composables';

const props = withDefaults(defineProps<VFormMultiselectBoxProps<T, ValueKey, LabelKey>>(), {
  sortButton: true,
});
const emit = defineEmits<VFormMultiselectBoxEmits<T>>();

const { innerValue } = useInputInnerValue(toRef(props, 'value'));

const valueType = computed<ValueKey>(() => props.optionValue || 'value' as ValueKey);
const labelType = computed<LabelKey>(() => props.optionLabel || 'label' as LabelKey);

const data = computed<IFormPlainDataListboxItem<T>[]>(() => {
  // Because of slow Array.includes method, create Set collection from selected values
  const selected = new Set<T>(innerValue.value);

  return props.options.map((item) => {
    const id = item[valueType.value] as T;
    const value = item[labelType.value] as string;

    return {
      id,
      value,
      check: selected.has(id),
    };
  });
});

function onChange(change: IFormPlainDataListboxPropsChangeState<T>): void {
  innerValue.value = change.values.map((item) => item.id);
  emit('input', innerValue.value);
  emit('change', innerValue.value);
}
</script>

<template>
  <VFormPlainDataListbox v-data-qa-auto-id="'VFormMultiselectBox'"
    :data="data"
    :readonly="readonly"
    :hide-errors="hideErrors"
    :disabled="disabled"
    :name="name"
    :errors="errors"
    :is-invalid="isInvalid"
    :label="label"
    :not-float-label="notFloatLabel"
    :required="required"
    :is-full-width="isFullWidth"
    :is-loading="isLoading"
    :disable-checkbox-filter="isFilterDisabled"
    :sort-button="sortButton"
    :sort-after-change-item="sortAfterChangeItem"
    :show-info-message="showInfoMessage"
    :info-message="infoMessage"
    :message-severity="messageSeverity"
    :message-icon="messageIcon"
    @change="onChange"
  />
</template>
